import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users, Search, Shield, ShieldOff, Trash2, Key,
    UserCheck, UserX, ChevronDown, Loader2, Copy, Mail, CheckCircle, AlertCircle,
    Activity, TrendingUp, Clock, UserPlus, ShieldAlert, Ban, RefreshCw,
    Trophy, Target, ChevronRight, ChevronDown as ChevronDownIcon, Flame, Infinity
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatWidget({ icon: Icon, label, value, sub, color = 'text-primary', bgColor = 'bg-primary/10', loading, onClick, active }) {
    return (
        <Card
            className={`cursor-default transition-all ${onClick ? 'cursor-pointer hover:border-primary/50' : ''} ${active ? 'border-primary ring-1 ring-primary/30' : ''}`}
            onClick={onClick}
        >
            <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <div className={`p-2 rounded-lg ${bgColor} flex-shrink-0`}>
                        <Icon className={`h-4 w-4 ${color}`} />
                    </div>
                    {loading ? (
                        <Skeleton className="h-7 w-12" />
                    ) : (
                        <span className="text-2xl font-black tabular-nums">{value ?? '—'}</span>
                    )}
                </div>
                <p className="text-sm font-semibold mt-3">{label}</p>
                {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
            </CardContent>
        </Card>
    );
}

export default function AdminUsersPage() {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showResetDialog, setShowResetDialog] = useState(false);
    const [showVerifyDialog, setShowVerifyDialog] = useState(false);
    const [tempPassword, setTempPassword] = useState('');
    const [processing, setProcessing] = useState(false);
    const [filterMode, setFilterMode] = useState('all'); // 'all' | 'unverified' | 'week' | 'disabled' | 'never' | 'participants'
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [compStats, setCompStats] = useState(null);
    const [compLoading, setCompLoading] = useState(true);
    const [expandedMatch, setExpandedMatch] = useState(null);

    useEffect(() => {
        if (!isAuthenticated || !user?.is_admin) {
            toast.error('Admin access required');
            navigate('/');
            return;
        }
        fetchUsers();
        fetchStats();
        fetchCompStats();
    }, [isAuthenticated, user, navigate]);

    const fetchCompStats = async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/admin/football-comp-stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCompStats(res.data);
        } catch (err) {
            console.error('Failed to fetch comp stats:', err);
        } finally {
            setCompLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/admin/user-stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(res.data);
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const token = getToken();
            const res = await axios.get(`${API_URL}/api/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(res.data.users || []);
        } catch (err) {
            console.error('Failed to fetch users:', err);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleAdmin = async (targetUser) => {
        setProcessing(true);
        try {
            const token = getToken();
            await axios.put(
                `${API_URL}/api/admin/users/${targetUser.id}`,
                { is_admin: !targetUser.is_admin },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`${targetUser.name} ${targetUser.is_admin ? 'demoted from' : 'promoted to'} admin`);
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update user');
        } finally {
            setProcessing(false);
        }
    };

    const handleToggleDisabled = async (targetUser) => {
        setProcessing(true);
        try {
            const token = getToken();
            await axios.put(
                `${API_URL}/api/admin/users/${targetUser.id}`,
                { is_disabled: !targetUser.is_disabled },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`${targetUser.name} account ${targetUser.is_disabled ? 'enabled' : 'disabled'}`);
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update user');
        } finally {
            setProcessing(false);
        }
    };

    const handleToggleNeverExpire = async (targetUser) => {
        setProcessing(true);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/users/${targetUser.id}/never-expire`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(res.data.message);
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update user');
        } finally {
            setProcessing(false);
        }
    };

    const handleVerifyEmail = async () => {
        if (!selectedUser) return;
        setProcessing(true);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/users/${selectedUser.id}/verify-email`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.already_verified) {
                toast.info(`${selectedUser.name} was already verified`);
            } else {
                toast.success(`${selectedUser.name}'s email has been verified`);
            }
            setShowVerifyDialog(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to verify user');
        } finally {
            setProcessing(false);
        }
    };

    const handleResetPassword = async () => {
        if (!selectedUser) return;
        setProcessing(true);
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_URL}/api/admin/users/${selectedUser.id}/reset-password`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setTempPassword(res.data.temporary_password);
            toast.success('Password reset successfully');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to reset password');
            setShowResetDialog(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;
        setProcessing(true);
        try {
            const token = getToken();
            await axios.delete(
                `${API_URL}/api/admin/users/${selectedUser.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`User ${selectedUser.email} deleted`);
            setShowDeleteDialog(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete user');
        } finally {
            setProcessing(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const filteredUsers = users.filter(u => {
        const matchesSearch = u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            u.email?.toLowerCase().includes(searchQuery.toLowerCase());

        let matchesFilter = true;
        if (filterMode === 'unverified') matchesFilter = !u.email_verified;
        else if (filterMode === 'disabled') matchesFilter = !!u.is_disabled;
        else if (filterMode === 'never') matchesFilter = !u.last_login;
        else if (filterMode === 'week') {
            const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
            matchesFilter = u.created_at && new Date(u.created_at) > weekAgo;
        } else if (filterMode === 'participants') {
            const ids = compStats?.participant_ids || [];
            matchesFilter = ids.includes(u.id);
        }

        return matchesSearch && matchesFilter;
    });

    if (loading) {
        return (
            <div className="container-app py-8">
                <Skeleton className="h-10 w-1/3 mb-8" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    return (
        <div className="container-app py-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-heading font-bold page-title">User Management</h1>
                    <p className="text-muted-foreground text-sm mt-1">Manage accounts, permissions and access</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => { fetchUsers(); fetchStats(); fetchCompStats(); }} disabled={loading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {/* Stat Widgets */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
                <StatWidget
                    icon={Users}
                    label="Total Users"
                    value={stats?.total ?? users.length}
                    sub="All registered accounts"
                    loading={statsLoading}
                    color="text-blue-500"
                    bgColor="bg-blue-500/10"
                />
                <StatWidget
                    icon={Activity}
                    label="Online Now"
                    value={stats?.online_now}
                    sub="Active in last 5 min"
                    loading={statsLoading}
                    color="text-green-500"
                    bgColor="bg-green-500/10"
                />
                <StatWidget
                    icon={Clock}
                    label="Active Today"
                    value={stats?.active_today}
                    sub="Seen in last 24 hours"
                    loading={statsLoading}
                    color="text-emerald-500"
                    bgColor="bg-emerald-500/10"
                />
                <StatWidget
                    icon={UserPlus}
                    label="New This Week"
                    value={stats?.new_this_week}
                    sub="Signed up in 7 days"
                    loading={statsLoading}
                    color="text-violet-500"
                    bgColor="bg-violet-500/10"
                    onClick={() => setFilterMode(filterMode === 'week' ? 'all' : 'week')}
                    active={filterMode === 'week'}
                />
                <StatWidget
                    icon={AlertCircle}
                    label="Unverified"
                    value={stats?.unverified ?? users.filter(u => !u.email_verified).length}
                    sub="Pending email verify"
                    loading={statsLoading}
                    color="text-amber-500"
                    bgColor="bg-amber-500/10"
                    onClick={() => setFilterMode(filterMode === 'unverified' ? 'all' : 'unverified')}
                    active={filterMode === 'unverified'}
                />
            </div>

            {/* Second row: smaller contextual stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                <StatWidget
                    icon={TrendingUp}
                    label="New This Month"
                    value={stats?.new_this_month}
                    sub="Last 30 days"
                    loading={statsLoading}
                    color="text-indigo-400"
                    bgColor="bg-indigo-500/10"
                />
                <StatWidget
                    icon={ShieldAlert}
                    label="Admins"
                    value={stats?.admins}
                    sub="Admin accounts"
                    loading={statsLoading}
                    color="text-orange-500"
                    bgColor="bg-orange-500/10"
                />
                <StatWidget
                    icon={Ban}
                    label="Disabled"
                    value={stats?.disabled}
                    sub="Suspended accounts"
                    loading={statsLoading}
                    color="text-red-500"
                    bgColor="bg-red-500/10"
                    onClick={() => setFilterMode(filterMode === 'disabled' ? 'all' : 'disabled')}
                    active={filterMode === 'disabled'}
                />
                <StatWidget
                    icon={UserX}
                    label="Never Logged In"
                    value={stats?.never_logged_in}
                    sub="Registered, never returned"
                    loading={statsLoading}
                    color="text-slate-400"
                    bgColor="bg-slate-500/10"
                    onClick={() => setFilterMode(filterMode === 'never' ? 'all' : 'never')}
                    active={filterMode === 'never'}
                />
            </div>

            {/* ── Football Competition Panel ── */}
            <Card className="mb-8 border-amber-500/20">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Trophy className="h-5 w-5 text-amber-500" />
                            <CardTitle className="text-base">
                                {compLoading ? 'Loading…' : compStats?.comp_name || 'Race for the World Cup'}
                            </CardTitle>
                        </div>
                        {compStats && (
                            <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-500">
                                {compStats.total_matches} matches total
                            </Badge>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-5">
                    {/* Top stat row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <button
                            className={`bg-muted/40 rounded-xl p-3 text-center w-full transition-all hover:bg-muted/70 ${filterMode === 'participants' ? 'ring-2 ring-amber-500/60 bg-amber-500/10' : ''}`}
                            onClick={() => setFilterMode(filterMode === 'participants' ? 'all' : 'participants')}
                            title="Click to filter user list to competition participants"
                        >
                            {compLoading ? <Skeleton className="h-7 w-12 mx-auto mb-1" /> : <p className="text-2xl font-black text-amber-500">{compStats?.total_participants ?? '—'}</p>}
                            <p className="text-xs text-muted-foreground font-medium">Participants</p>
                            <p className="text-[10px] text-amber-500/70 mt-0.5">click to filter ↓</p>
                        </button>
                        <div className="bg-muted/40 rounded-xl p-3 text-center">
                            {compLoading ? <Skeleton className="h-7 w-12 mx-auto mb-1" /> : <p className="text-2xl font-black text-blue-500">{compStats?.total_picks ?? '—'}</p>}
                            <p className="text-xs text-muted-foreground font-medium">Total Picks Made</p>
                        </div>
                        <div className="bg-muted/40 rounded-xl p-3 text-center">
                            {compLoading ? <Skeleton className="h-7 w-12 mx-auto mb-1" /> : <p className="text-2xl font-black text-green-500">{compStats?.avg_picks_per_user ?? '—'}</p>}
                            <p className="text-xs text-muted-foreground font-medium">Avg Picks / User</p>
                        </div>
                        <div className="bg-muted/40 rounded-xl p-3 text-center">
                            {compLoading ? <Skeleton className="h-7 w-12 mx-auto mb-1" /> : <p className="text-2xl font-black text-violet-500">{compStats?.settled_picks ?? '—'}</p>}
                            <p className="text-xs text-muted-foreground font-medium">Settled Picks</p>
                        </div>
                    </div>

                    {/* Today's matches & missing picks */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                            <Flame className="h-3.5 w-3.5 text-orange-500" /> Today's Matches — Missing Picks
                        </p>
                        {compLoading ? (
                            <div className="space-y-2">{[1,2].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
                        ) : compStats?.today_missing_alerts?.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-3 text-center">No matches today</p>
                        ) : (
                            <div className="space-y-2">
                                {compStats.today_missing_alerts.map(m => {
                                    // Only active (non-eliminated) players are eligible to pick
                                    const eligible = m.eligible_count ?? compStats.active_participants ?? compStats.total_participants;
                                    const pct = eligible > 0
                                        ? Math.round((m.predicted_count / eligible) * 100) : 0;
                                    const isExpanded = expandedMatch === m.pa_match_id;
                                    const kickoff = m.kickoff_time ? new Date(m.kickoff_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                                    return (
                                        <div key={m.pa_match_id} className="border border-border rounded-xl overflow-hidden">
                                            <button
                                                className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors text-left"
                                                onClick={() => setExpandedMatch(isExpanded ? null : m.pa_match_id)}
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <span className="font-semibold text-sm truncate">
                                                        {m.home_team} vs {m.away_team}
                                                    </span>
                                                    {kickoff && <span className="text-xs text-muted-foreground flex-shrink-0">{kickoff}</span>}
                                                    <Badge variant="outline" className={`text-xs flex-shrink-0 ${m.status === 'scheduled' ? 'border-green-500/40 text-green-500' : 'border-amber-500/40 text-amber-500'}`}>
                                                        {m.status || 'scheduled'}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                                                    <div className="text-right">
                                                        <p className="text-sm font-bold">{m.predicted_count}<span className="text-muted-foreground font-normal">/{eligible} picked</span></p>
                                                        {m.missing_count > 0 && (
                                                            <p className="text-xs text-red-400 font-semibold">{m.missing_count} missing</p>
                                                        )}
                                                    </div>
                                                    {/* Progress bar */}
                                                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                                        <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                                    </div>
                                                    {m.missing_count > 0
                                                        ? isExpanded ? <ChevronDownIcon className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                        : <CheckCircle className="h-4 w-4 text-green-500" />
                                                    }
                                                </div>
                                            </button>
                                            {/* Expanded: list of missing users */}
                                            {isExpanded && m.missing_count > 0 && (
                                                <div className="border-t border-border bg-muted/20 px-4 py-3">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <p className="text-xs font-semibold text-muted-foreground">
                                                            Users yet to pick ({m.missing_total}):
                                                        </p>
                                                        <button
                                                            onClick={() => {
                                                                const emails = m.missing_users.map(u => u.email).filter(Boolean).join('; ');
                                                                navigator.clipboard.writeText(emails);
                                                                toast.success('Email addresses copied — paste into Outlook To: field');
                                                            }}
                                                            className="text-xs bg-blue-500/10 text-blue-400 border border-blue-500/30 rounded px-2 py-1 hover:bg-blue-500/20 transition-colors"
                                                        >
                                                            Copy email addresses
                                                        </button>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {m.missing_users.map(u => (
                                                            <span key={u.id} className="inline-flex items-center gap-1 text-xs bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg px-2 py-0.5">
                                                                {u.name || u.email}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Top pickers */}
                    {compStats?.top_pickers?.length > 0 && (
                        <div>
                            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                                <Target className="h-3.5 w-3.5 text-blue-500" /> Most Active Participants
                            </p>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-muted-foreground border-b">
                                            <th className="text-left pb-2 font-medium">#</th>
                                            <th className="text-left pb-2 font-medium">User</th>
                                            <th className="text-center pb-2 font-medium">Picks</th>
                                            <th className="text-center pb-2 font-medium">Points</th>
                                            <th className="text-center pb-2 font-medium">Exact</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {compStats.top_pickers.map((p, i) => (
                                            <tr key={p.user_id} className="border-b border-border/30 last:border-0">
                                                <td className="py-1.5 text-muted-foreground">{i + 1}</td>
                                                <td className="py-1.5 font-medium">{p.name}</td>
                                                <td className="py-1.5 text-center tabular-nums">{p.picks}</td>
                                                <td className="py-1.5 text-center tabular-nums font-bold text-amber-500">{p.points}</td>
                                                <td className="py-1.5 text-center tabular-nums text-green-500">{p.exact}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Active filter badge */}
            {filterMode !== 'all' && (
                <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="text-xs border-primary text-primary">
                        Filtered: {filterMode === 'unverified' ? 'Unverified' : filterMode === 'week' ? 'New This Week' : filterMode === 'disabled' ? 'Disabled' : filterMode === 'participants' ? `Race for the World Cup (${compStats?.total_participants ?? 0})` : 'Never Logged In'}
                    </Badge>
                    <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setFilterMode('all')}>
                        Clear filter ×
                    </button>
                </div>
            )}

            {/* Search */}
            <Card className="mb-6">
                <CardContent className="pt-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            data-testid="user-search"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Users Table */}
            <Card>
                <CardHeader>
                    <CardTitle>All Users</CardTitle>
                    <CardDescription>
                        Click on actions to manage user accounts
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <ScrollArea className="h-[500px]">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>User</TableHead>
                                    <TableHead>Company</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Verified</TableHead>
                                    <TableHead>Joined</TableHead>
                                    <TableHead>Last Login</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUsers.map(u => (
                                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                        <TableCell>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">{u.name}</span>
                                                    {u.is_admin && (
                                                        <Badge className="bg-primary/20 text-primary">Admin</Badge>
                                                    )}
                                                    {u.festival_guest && (
                                                        <Badge className="bg-blue-500/20 text-blue-500">Guest</Badge>
                                                    )}
                                                </div>
                                                <span className="text-sm text-muted-foreground">{u.email}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {u.company || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {u.is_disabled ? (
                                                <Badge variant="destructive">Disabled</Badge>
                                            ) : (
                                                <Badge className="bg-green-500/20 text-green-500">Active</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {u.email_verified ? (
                                                <Badge className="bg-green-500/20 text-green-500">
                                                    <CheckCircle className="h-3 w-3 mr-1" />
                                                    Verified
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-amber-500/20 text-amber-500">
                                                    <AlertCircle className="h-3 w-3 mr-1" />
                                                    Unverified
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center justify-end gap-1">
                                                {/* Verify Email - only show if not verified */}
                                                {!u.email_verified && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => {
                                                            setSelectedUser(u);
                                                            setShowVerifyDialog(true);
                                                        }}
                                                        disabled={processing}
                                                        title="Verify email manually"
                                                        className="text-amber-500 hover:text-amber-600"
                                                    >
                                                        <CheckCircle className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                
                                                {/* Toggle Admin */}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleToggleAdmin(u)}
                                                    disabled={processing || u.id === user?.id}
                                                    title={u.is_admin ? 'Remove admin' : 'Make admin'}
                                                >
                                                    {u.is_admin ? (
                                                        <ShieldOff className="h-4 w-4 text-amber-500" />
                                                    ) : (
                                                        <Shield className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                
                                                {/* Never Expire (kiosk/display accounts) */}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleToggleNeverExpire(u)}
                                                    disabled={processing}
                                                    title={u.never_expire ? 'Remove never-expire (kiosk)' : 'Set never-expire (kiosk/display)'}
                                                >
                                                    <Infinity className={`h-4 w-4 ${u.never_expire ? 'text-purple-400' : ''}`} />
                                                </Button>

                                                {/* Toggle Disabled */}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleToggleDisabled(u)}
                                                    disabled={processing || u.id === user?.id}
                                                    title={u.is_disabled ? 'Enable account' : 'Disable account'}
                                                >
                                                    {u.is_disabled ? (
                                                        <UserCheck className="h-4 w-4 text-green-500" />
                                                    ) : (
                                                        <UserX className="h-4 w-4" />
                                                    )}
                                                </Button>
                                                
                                                {/* Reset Password */}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => {
                                                        setSelectedUser(u);
                                                        setTempPassword('');
                                                        setShowResetDialog(true);
                                                    }}
                                                    disabled={processing}
                                                    title="Reset password"
                                                >
                                                    <Key className="h-4 w-4" />
                                                </Button>
                                                
                                                {/* Delete */}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => {
                                                        setSelectedUser(u);
                                                        setShowDeleteDialog(true);
                                                    }}
                                                    disabled={processing || u.id === user?.id}
                                                    title="Delete user"
                                                    className="text-destructive hover:text-destructive"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredUsers.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                            No users found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ScrollArea>
                </CardContent>
            </Card>

            {/* Verify Email Dialog */}
            <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Verify User Email</DialogTitle>
                        <DialogDescription>
                            Manually verify {selectedUser?.name}'s email address ({selectedUser?.email})?
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <p className="text-sm text-amber-600">
                                This will mark the user's email as verified, allowing them to log in without completing email verification.
                                Use this if the user is having trouble receiving verification emails.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowVerifyDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleVerifyEmail} disabled={processing} className="bg-amber-500 hover:bg-amber-600">
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                            Verify Email
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reset Password</DialogTitle>
                        <DialogDescription>
                            {tempPassword 
                                ? `A temporary password has been generated for ${selectedUser?.name}`
                                : `Generate a temporary password for ${selectedUser?.name}?`
                            }
                        </DialogDescription>
                    </DialogHeader>
                    
                    {tempPassword ? (
                        <div className="space-y-4 py-4">
                            <div className="p-4 bg-muted rounded-lg">
                                <p className="text-xs text-muted-foreground mb-2">Temporary Password</p>
                                <div className="flex items-center justify-between">
                                    <code className="text-lg font-mono font-bold">{tempPassword}</code>
                                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(tempPassword)}>
                                        <Copy className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                <p className="text-sm text-amber-600">
                                    Share this password securely with the user. They should change it after logging in.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">{selectedUser?.email}</span>
                                <Button size="sm" variant="ghost" onClick={() => copyToClipboard(selectedUser?.email)}>
                                    <Copy className="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    ) : null}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowResetDialog(false)}>
                            {tempPassword ? 'Done' : 'Cancel'}
                        </Button>
                        {!tempPassword && (
                            <Button onClick={handleResetPassword} disabled={processing}>
                                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Reset Password
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete User Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete User</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to permanently delete {selectedUser?.name} ({selectedUser?.email})?
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                            <p className="text-sm text-destructive">
                                This will permanently delete the user account and all associated data including:
                            </p>
                            <ul className="text-sm text-destructive mt-2 list-disc list-inside">
                                <li>Competition entries and picks</li>
                                <li>Stable favorites</li>
                                <li>Settings and preferences</li>
                            </ul>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteUser} disabled={processing}>
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Delete Permanently
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
